import { useEffect, useMemo } from "react";
import { MiButton, MiDetailPageHeader, MiTab } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { HoldReasons } from "./tabs/HoldReasons/HoldReasons";
import { WorkOrderTemplates } from "./tabs/WorkOrderTemplates/WorkOrderTemplates";
import { OrderTemplateAssignmentTemplateModal } from "./OrderTemplateAssignmentTemplateModal";
import { AssignmentTemplatesTab, Details } from "./tabs";
import { useOrderTemplateViewModel } from "./useOrderTemplateViewModel";

const ROUTE_NAMES = {
  DETAILS: "details",
  TEMPLATES: "assignment-templates",
  HOLD_REASONS: "hold-reasons",
  WORK_ORDER_TEMPLATES: "work-order-types",
};

const TAB_NAMES = {
  DETAILS: "Details",
  TEMPLATES: "Assignment Templates",
  HOLD_REASONS: "Hold Reasons",
  WORK_ORDER_TEMPLATES: ENTITY_NAMES.WORK_ORDER_TEMPLATES,
};

const TAB_LIST = [
  {
    name: TAB_NAMES.DETAILS,
    value: ROUTE_NAMES.DETAILS,
    label: TAB_NAMES.DETAILS,
  },
  {
    name: TAB_NAMES.TEMPLATES,
    value: ROUTE_NAMES.TEMPLATES,
    label: TAB_NAMES.TEMPLATES,
  },
  {
    name: TAB_NAMES.HOLD_REASONS,
    value: ROUTE_NAMES.HOLD_REASONS,
    label: TAB_NAMES.HOLD_REASONS,
  },
  {
    name: TAB_NAMES.WORK_ORDER_TEMPLATES,
    value: ROUTE_NAMES.WORK_ORDER_TEMPLATES,
    label: TAB_NAMES.WORK_ORDER_TEMPLATES,
  },
];

export function OrderTemplate() {
  const router = useRouter();
  const { id: orderTemplateId, activeTab } = router.params;

  const {
    orderTemplate,
    loading,
    isModalOpen,
    getAllAssignmentTemplates,
    handleAddAssignmentTemplate,
    closeModal,
    openModal,
    getOrderTemplateDetails,
    onDetailsUpdate,
    getTrades,
    trades,
    workOrderTemplateModalOpen,
    openWorkOrderTemplateModal,
  } = useOrderTemplateViewModel({ orderTemplateId });

  useEffect(() => {
    getOrderTemplateDetails();
    getTrades();
  }, [orderTemplateId]);

  const actionComponent = useMemo(() => {
    switch (activeTab) {
      case ROUTE_NAMES.TEMPLATES:
        return (
          <MiButton
            startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={openModal}
            data-testid="add-btn"
            title="Add Assignment Template"
          />
        );
      case ROUTE_NAMES.WORK_ORDER_TEMPLATES:
        return (
          <MiButton
            startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={() => openWorkOrderTemplateModal(true)}
            data-testid="add-btn"
            title={`Add ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
          />
        );
      default:
        return null;
    }
  }, [activeTab]);

  const handleTabChange = (tab) => {
    router.navigate(
      `/settings/order-management/order-types/${orderTemplateId}/${tab}`
    );
  };

  const customOnBack = () => {
    router.navigate(`/settings/order-management/order-types`);
  };
  return (
    <>
      <MiDetailPageHeader
        loading={loading}
        title={`${ENTITY_NAMES.ORDER_TEMPLATE}: ${orderTemplate?.name || ""}`}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        actionComponent={actionComponent}
        customOnBack={customOnBack}
      />
      <MiTab
        tabs={TAB_LIST}
        currentTab={activeTab}
        onTabChange={handleTabChange}
      />
      {activeTab === ROUTE_NAMES.DETAILS && (
        <Details
          orderTemplate={orderTemplate}
          trades={trades}
          onUpdate={onDetailsUpdate}
        />
      )}
      {activeTab === ROUTE_NAMES.TEMPLATES && (
        <AssignmentTemplatesTab
          orderTemplate={orderTemplate}
          refresh={getOrderTemplateDetails}
        />
      )}
      {activeTab === ROUTE_NAMES.HOLD_REASONS && (
        <HoldReasons
          isLoading={loading}
          orderTemplate={orderTemplate}
          refresh={getOrderTemplateDetails}
          onUpdate={onDetailsUpdate}
        />
      )}
      {activeTab === ROUTE_NAMES.WORK_ORDER_TEMPLATES && (
        <WorkOrderTemplates
          orderTemplate={orderTemplate}
          refresh={getOrderTemplateDetails}
          workOrderTemplateModalOpen={workOrderTemplateModalOpen}
          closeWorkOrderTemplateModal={() => openWorkOrderTemplateModal(false)}
        />
      )}

      <OrderTemplateAssignmentTemplateModal
        open={isModalOpen}
        getAllAssignmentTemplates={getAllAssignmentTemplates}
        title="Add Assignment Template"
        buttonTitle="Submit"
        onClose={closeModal}
        onSubmit={handleAddAssignmentTemplate}
        loading={loading}
      />
    </>
  );
}
